import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '@/stores';

const routes = [
  {
    meta: { title: 'Origin', requiresAuth: false },
    path: '/',
    name: 'origin',
    redirect: '/dashboard',
  },
  {
    meta: { title: 'Login', requiresAuth: false },
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
  },
  {
    meta: { title: 'Request password reset', requiresAuth: false },
    path: '/request-password-reset',
    name: 'requestPasswordReset',
    component: () => import('@/pages/RequestPasswordRequestPage.vue'),
  },
  {
    meta: { title: 'Password reset', requiresAuth: false },
    path: '/password-reset',
    name: 'passwordReset',
    component: () => import('@/pages/ResetPasswordPage.vue'),
  },
  {
    meta: { title: 'Logout', requiresAuth: false },
    path: '/logout',
    name: 'logout',
    component: () => import('@/pages/LogoutPage.vue'),
  },
  {
    path: '/no-permission',
    name: 'no-permission',
    component: () => import('@/pages/NoPermissionPage.vue'),
    meta: { requiresAuth: false, title: 'Access Denied' },
  },
  {
    meta: {
      title: 'Dashboard',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
  },
  {
    meta: {
      title: 'Analytics',
      requiresAuth: true,
      roles: ['SUPER_ADMIN'],
    },
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/pages/AnalyticsPage.vue'),
  },
  {
    meta: {
      title: 'Pricing',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/pages/PricingPage.vue'),
  },
  {
    meta: {
      title: 'Onboarding',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/pages/onboarding/OnboardingPage.vue'),
    children: [
      {
        path: '',
        name: 'onboarding.list',
        component: () => import('@/pages/onboarding/OnboardingList.vue'),
      },
      {
        path: ':id',
        name: 'onboarding.edit',
        component: () => import('@/pages/onboarding/OnboardingEdit.vue'),
      },
    ],
  },
  {
    meta: {
      title: 'Drivers',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/drivers',
    name: 'drivers',
    component: () => import('@/pages/drivers/DriversPage.vue'),
    children: [
      {
        path: '',
        name: 'drivers.list',
        component: () => import('@/pages/drivers/DriversList.vue'),
      },
      {
        path: ':id',
        name: 'drivers.edit',
        component: () => import('@/pages/drivers/DriverEdit.vue'),
        children: [
          {
            path: 'information',
            name: 'drivers.information',
            component: () => import('@/components/drivers/DriverInformation.vue'),
          },
          {
            path: 'documents',
            name: 'drivers.documents',
            component: () => import('@/components/drivers/DriverDocuments.vue'),
          },
          {
            path: 'cars',
            name: 'drivers.cars',
            component: () => import('@/components/drivers/DriverCars.vue'),
            props: { showDocuments: true },
          },
          {
            path: 'reviews',
            name: 'drivers.reviews',
            component: () => import('@/components/drivers/DriverReviews.vue'),
          },
        ],
      },
    ],
  },
  {
    meta: {
      title: 'Riders',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/riders',
    name: 'riders',
    component: () => import('@/pages/riders/RidersPage.vue'),
    children: [
      {
        path: '',
        name: 'riders.list',
        component: () => import('@/pages/riders/RidersList.vue'),
      },
      {
        path: ':id',
        name: 'riders.edit',
        component: () => import('@/pages/riders/RiderEdit.vue'),
        children: [
          {
            path: 'information',
            name: 'riders.information',
            component: () => import('@/components/riders/RiderInformation.vue'),
          },
          {
            path: 'reviews',
            name: 'riders.reviews',
            component: () => import('@/components/riders/RiderReviews.vue'),
          },
        ],
      },
    ],
  },
  {
    meta: {
      title: 'Rides',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/rides',
    name: 'rides',
    component: () => import('@/pages/rides/RidesPage.vue'),
    children: [
      {
        path: '',
        name: 'rides.list',
        component: () => import('@/pages/rides/RidesList.vue'),
      },
      {
        path: ':id',
        name: 'rides.edit',
        component: () => import('@/pages/rides/RideEdit.vue'),
      },
    ],
  },
  {
    meta: {
      title: 'Payments',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/payments',
    name: 'payments',
    component: () => import('@/pages/payments/PaymentsPage.vue'),
    children: [
      {
        path: '',
        name: 'payments.list',
        component: () => import('@/pages/payments/PaymentsList.vue'),
      },
    ],
  },
  {
    meta: {
      title: 'Support',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/support',
    name: 'support',
    component: () => import('@/pages/SupportPage.vue'),
  },
  {
    meta: {
      title: 'Settings',
      requiresAuth: true,
      roles: ['ADMIN', 'SUPER_ADMIN'],
    },
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/settings/SettingsPage.vue'),
    children: [
      {
        path: '',
        name: 'settings.admins',
        component: () => import('@/pages/settings/AdminsPage.vue'),
      },
      {
        path: 'pricing',
        name: 'settings.pricing',
        component: () => import('@/pages/settings/PricingPage.vue'),
      },
      {
        path: 'special-zones',
        name: 'settings.special-zones',
        component: () => import('@/pages/settings/SpecialZonesPage.vue'),
      },
    ],
  },
  {
    path: '/profile',
    component: () => import('@/pages/profile/ProfilePage.vue'),
    children: [
      { path: '', component: () => import('@/pages/profile/GeneralInfoPage.vue') },
      { path: 'security', component: () => import('@/pages/profile/SecurityPage.vue') },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  auth.error = null;

  // Ensure we know if a session exists before routing (memoized — resolves
  // once per app lifetime, then reads reactive state).
  await auth.init();

  const isLoggedIn = auth.isLoggedIn;
  // Role comes from the Better Auth session user — no extra request needed.
  const userRole = auth.role;

  // Public routes (no auth required)
  if (to.meta.requiresAuth === false || to.name === 'login') {
    // Prevent logged-in users from visiting login page again
    if (to.name === 'login' && isLoggedIn) {
      return { name: 'dashboard' };
    }
    return true;
  }

  if (!isLoggedIn) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // Role-based access control
  const allowedRoles = to.meta?.roles;
  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(userRole)) {
    return { name: 'no-permission' };
  }

  return true;
});

export default router;
